import React from 'react';
import { gettext } from '@eventbrite/i18n';
import { Button, CheckChunkierIcon, Typography } from '@eventbrite/marmalade';
import { getPricesForTLD, getTld } from './pricingAndPackagingUtils';
import getDiscovered from '../static/images/getDiscovered.webp';
import buildBuzz from '../static/images/buildBuzz.webp';
import engageAttendees from '../static/images/engageAttendees.webp';
import styles from './InfoCards.module.scss';

const getInfoCards = () => {
    const priceMapping = getPricesForTLD();
    const locale = Intl.NumberFormat().resolvedOptions().locale;
    return [
        {
            image: getDiscovered,
            headerText: gettext('Get discovered'),
            bodyContent: (
                <>
                    <p>
                        {gettext(
                            'Eventbrite’s powerful discovery marketplace connects local ticket-buyers to your events. And, with our built-in marketing suite powered by exclusive event data, you can effectively promote your events without leaving Eventbrite.',
                        )}
                    </p>
                </>
            ),
            checkListData: [
                gettext('Visibility on Eventbrite’s marketplace'),
                gettext('Eventbrite Ads*'),
                gettext('Email campaigns'),
                gettext('Facebook and Instagram ad tools'),
                gettext('Promo codes'),
                gettext('Smart audiences'),
                gettext('Data insights and reporting'),
            ],
        },
        {
            image: buildBuzz,
            headerText: gettext('Fast-track ticket sales'),
            bodyContent: (
                <>
                    <p>
                        {gettext(
                            'Our best-in-class ticketing tools make it easy to set up your events and get paid fast. With flexible payment options and streamlined checkout, your attendees can buy with confidence.',
                        )}
                    </p>
                </>
            ),
            checkListData: [
                gettext('Custom pages and multiple ticket types'),
                gettext('AI event creation'),
                gettext('Secure payment processing'),
                gettext('Pre-event payouts'),
                gettext('Embedded checkout'),
                gettext('Custom registration forms'),
                gettext('Sell add-ons'),
            ],
        },
        {
            image: engageAttendees,
            headerText: gettext('Engage attendees'),
            bodyContent: (
                <>
                    <p>
                        {gettext(
                            'Make event day and beyond a breeze with on-the-go check-in tools and attendee marketing automations, so your event shines from start to finish, plus event support when you need it.',
                        )}
                    </p>
                </>
            ),
            checkListData: [
                gettext('Automated event reminders'),
                gettext('Organizer app'),
                gettext('Mobile check-in'),
                gettext('Waitlists'),
                gettext('Help center'),
                gettext('Email support'),
                gettext('24/7 chat support for paid events'),
            ],
        },
    ];
};

export const InfoCardCheckList = ({ content }) => {
    return (
        <div className={styles.checklist_item}>
            <CheckChunkierIcon size="xsmall" />
            <Typography
                variant="body-sm"
                as="span"
                className={styles.checklist_item_text}
            >
                {content}
            </Typography>
        </div>
    );
};

export const InfoCard = ({ image, headerText, bodyContent, checkListData }) => {
    return (
        <div className={styles.pricing_and_packaging_info_card}>
            <div className={styles.image_div}>
                <img
                    src={image}
                    className={styles.info_card_image}
                    loading="lazy"
                />
            </div>
            <div className={styles.card_data}>
                <Typography
                    variant="heading-sm"
                    className={styles.info_card_header}
                >
                    {headerText}
                </Typography>
                <Typography
                    variant="body-md"
                    className={styles.info_card_description}
                >
                    {bodyContent}
                </Typography>
            </div>
            <div className={styles.card_checklist}>
                {checkListData.map((item) => (
                    <InfoCardCheckList content={item} key={item} />
                ))}
            </div>
        </div>
    );
};

export const InfoCards = () => {
    return (
        <div className={styles.pricing_and_packaging_cards_parent}>
            <div className={styles.title_div}>
                <Typography
                    variant="heading-md"
                    className={styles.cards_title}
                    as="span"
                >
                    {gettext('Your #1 partner in event success')}
                </Typography>
                <Typography
                    variant="body-lg"
                    className={styles.cards_description}
                >
                    {gettext(
                        'We put event management on auto-pilot, so you can focus on creating unforgettable experiences',
                    )}
                </Typography>
            </div>
            <div className={styles.pricing_and_packaging_cards}>
                {getInfoCards().map((cardProps, i) => (
                    <InfoCard key={i} {...cardProps} />
                ))}
            </div>
            <div className={styles.ctaSection}>
                <Button
                    size="responsive"
                    variant="secondary"
                    onClick={() => {
                        window.location.href = '/organizer/overview/';
                    }}
                >
                    {gettext('Learn more about our features')}
                </Button>
            </div>
            <Typography
                variant="body-sm"
                className={styles.info_card_disclaimer}
            >
                {gettext(
                    '*Eventbrite Ads is only available in select markets. Ad spend for advertising tools not included.',
                )}
            </Typography>
        </div>
    );
};

export default InfoCards;
